import { Combobox } from 'components/UI/combobox/combobox'
import dialogConfirm from 'components/dialogConfirm'
import { notifySuccess } from 'services/toast'
import { toast } from 'react-toastify'
import { useBulkAssignBrand } from 'services/brandsApi'
import { useBrandOptions } from 'hooks/useBrandOptions'

export const MAX_BULK_ASSIGN_LIMIT = 30

/** Props for the LinkBrandCombobox bulk action component. */
export interface LinkBrandComboboxProps {
  selectedVariantIds: number[]
  unselectedVariantIds: number[]
  totalSelectedRows: number
  isAllRowsSelected: boolean
  filters: Record<string, any>
  onSuccess?: () => void
  buttonLess?: boolean
}

/**
 * Combobox for bulk assigning a brand to selected variants.
 * Supports both explicit (variant_ids) and select-all (filters) modes.
 */
export function LinkBrandCombobox(props: LinkBrandComboboxProps) {
  const bulkAssignMutation = useBulkAssignBrand()
  const { brandOptions, isLoading } = useBrandOptions()

  async function handleSubmit(brandId: number | string) {
    // Enforce limit for explicit mode (non-select-all)
    if (
      !props.isAllRowsSelected &&
      props.selectedVariantIds.length > MAX_BULK_ASSIGN_LIMIT
    ) {
      toast.warning(
        `Cannot assign brand to more than ${MAX_BULK_ASSIGN_LIMIT} variants at a time. Please select fewer variants.`
      )
      return
    }

    const selectedBrand = brandOptions.find((b) => b.value === Number(brandId))
    const brandName = selectedBrand?.label || 'selected brand'

    const confirmed = await dialogConfirm(
      `Assign "${brandName}" to ${props.totalSelectedRows} variant${props.totalSelectedRows !== 1 ? 's' : ''}?`,
      'Assign Brand'
    )

    if (!confirmed) return

    try {
      const requestBody = props.isAllRowsSelected
        ? {
            brand_id: Number(brandId),
            filters: props.filters,
            exclude_ids: props.unselectedVariantIds,
          }
        : {
            brand_id: Number(brandId),
            variant_ids: props.selectedVariantIds,
          }

      const response = await bulkAssignMutation.mutateAsync(requestBody)

      const updatedCount = response?.updated_count ?? props.totalSelectedRows
      notifySuccess(
        `Successfully assigned brand to ${updatedCount} variant${updatedCount !== 1 ? 's' : ''}`
      )

      props.onSuccess?.()
    } catch (error) {
      console.error('Error in bulk assign brand:', error)
      toast.error('Failed to assign brand. Please try again.')
    }
  }

  return (
    <Combobox
      options={brandOptions}
      searchPlaceholder="Search brands..."
      placeholder="Assign Brand"
      onSelectAltAction={handleSubmit}
      loading={isLoading}
      buttonLess={props.buttonLess}
      notFoundPlaceholder="No brands found"
    />
  )
}
